/**
 * Display formatters the home surface input needs.
 *
 * Shared with the API for the same reason as the calendar helpers: both callers
 * must render byte-identical labels, and every one of these takes its zone as
 * an explicit argument (or is UTC-pinned / purely lexical), so there is no
 * host-only input to justify two copies.
 */

import { DateTime } from 'luxon';

/**
 * Abbreviate a points balance for the compact header pill (e.g. 35000 → "35k",
 * 35200 → "35.2k"); values under a thousand render in full.
 */
export function formatPoints(points: number): string {
  if (!Number.isFinite(points)) return '0';
  const rounded = Math.round(points);
  if (Math.abs(rounded) < 1000) return String(rounded);
  const thousands = Math.round((rounded / 1000) * 10) / 10;
  return `${thousands}k`;
}

// Locale-format a raw `slotDate` (a UTC calendar day) for display, e.g.
// `2026-07-07` → `Tuesday, July 7`. UTC-pinned so the label never shifts with
// the reader's timezone.
const CARE_SLOT_LABEL_FORMAT = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  timeZone: 'UTC',
});

/** The human-readable care-call slot label for a `YYYY-MM-DD` slot date. */
export function formatCareSlotLabel(slotDate: string): string {
  return CARE_SLOT_LABEL_FORMAT.format(new Date(`${slotDate}T00:00:00.000Z`));
}

/** Sentence-case a slug/enum value (`care_specialist` → `Care specialist`).
 * The role pills render this so the resolved server role paints the same string
 * as the sentence-cased locale fallback shown while the config loads. */
export function sentenceCase(value: string): string {
  const words = value
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  const sentence = words.join(' ').toLowerCase();
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

// `scheduledFor` is a true UTC instant minted by the scheduling API (specialist
// availability expanded server-side), so a member-facing label must localise it
// to the member's own zone — the same instant reads "12:45pm" in UTC and
// "1:45pm" in London summer time, and the member picked the latter. The zone is
// therefore an explicit argument (ADR-096: name the zone, never the ambient
// host). The `UTC` default exists for callers that have no member zone to
// offer — it is the honest "no localisation" answer, not a claim about the
// member — so a server caller that can resolve `members.timezone` must pass it.
const APPOINTMENT_LOCALE = 'en-US';

/** Explicit presentation data; native preference discovery belongs to the client. */
export interface ClockPresentation {
  readonly hourCycle: 'h12' | 'h23';
  readonly locale: string;
}

/** The member clock policy shared by instant labels and reminder wall clocks. */
export function formatClockTime(at: DateTime, clock: ClockPresentation): string {
  const localized = at.setLocale(clock.locale);
  // Locale parts are empty for invalid values; retain the existing fallback.
  if (!localized.isValid) return localized.toFormat('h:mm a');
  // Keep the established 24-hour digit convention; let the locale decide
  // where a 12-hour day period belongs (for example, before the time in ja/ko).
  return clock.hourCycle === 'h23'
    ? localized.toFormat('HH:mm')
    : localized
        .toLocaleParts({ hour: 'numeric', minute: '2-digit', hourCycle: 'h12' })
        .map((part) => part.value)
        .join('')
        .replace(/[\u00a0\u202f]/g, ' ');
}

/**
 * Catalogue templates that name an appointment's day relative to `now`. Each
 * receives the absolute label and wraps it, e.g. `Tomorrow, {{when}}`.
 */
export interface AppointmentRelativeDay {
  readonly now: Date;
  readonly today: (when: string) => string;
  readonly tomorrow: (when: string) => string;
}

/**
 * Formats the appointment instant for call-card display in the given zone,
 * e.g. `Fri 24 Jul · 9:00 AM`. With `relative`, a call on the member-local
 * day of `now` or the day after reads `Today, …` or `Tomorrow, …`.
 *
 * The `en-US` locale tag is the ADR-017 Rule 10 phase-1 posture (`en` only);
 * this helper is the shared seam to replace when the locale catalogue gains
 * date-time formats.
 */
export function formatAppointmentWhen(
  scheduledFor: string,
  zone = 'UTC',
  relative?: AppointmentRelativeDay,
  clock: ClockPresentation = { hourCycle: 'h12', locale: APPOINTMENT_LOCALE },
): string {
  const at = DateTime.fromISO(scheduledFor, { zone }).setLocale(APPOINTMENT_LOCALE);
  if (!at.isValid) return '';
  const when = `${at.toFormat('ccc d LLL')} · ${formatClockTime(at, clock)}`;
  if (relative === undefined) return when;
  const today = DateTime.fromJSDate(relative.now, { zone });
  if (at.hasSame(today, 'day')) return relative.today(when);
  if (at.hasSame(today.plus({ days: 1 }), 'day')) return relative.tomorrow(when);
  return when;
}

/** Derives a compact avatar fallback from the provider display name. */
export function providerInitials(providerName: string): string {
  const [primaryName = ''] = providerName.split(',');
  return primaryName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}
